import React, { useEffect, useRef, useState } from 'react';
import { MeetingMinutesTaskSummary } from '../../api-client/autogenerated';
import { getMeetingMinutesTaskSummary } from '../../models/api/meeting-minutes-tasks';
import StatusPill from '../custom-components/StatusPill';

interface Props {
  projectId: string;
  documentId: string;
}

const POLL_INTERVAL_MS = 4000;
const MAX_POLLS = 15;

const GRAY = '#616061';
const BLUE = '#0947B9';
const AMBER = '#F28B00';
// Matches the "done" green used by getTaskStatusColors for completed/accepted statuses.
const GREEN = '#2BB073';

/**
 * "Tasks Extracted" cell for the Meeting Minutes / Design Meeting Minutes tables:
 * - gray "Pending" while the file is still being ingested (no transcript yet);
 * - blue "Ready for Extraction" once ingested (extraction starts immediately and this holds
 *   until tasks are published);
 * - amber "[unpublished]/[total] Unpublished Tasks" while at least one task is published but
 *   some drafts still need to be published or discarded;
 * - green "[published] Tasks Published" once nothing is left to publish or discard.
 *
 * Polls while still ingesting so the cell advances to "Ready for Extraction" on its own.
 */
export default function MeetingMinutesTasksExtractedCell({ projectId, documentId }: Props) {
  const [summary, setSummary] = useState<MeetingMinutesTaskSummary | null>(null);
  const pollCount = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const result = await getMeetingMinutesTaskSummary(projectId, documentId);
        if (cancelled) return;
        setSummary(result);
        // Keep polling while the file is still being ingested, so "Pending" advances to
        // "Ready for Extraction" once its transcript is ready.
        if (!result.ingested && pollCount.current < MAX_POLLS) {
          pollCount.current += 1;
          timer.current = setTimeout(load, POLL_INTERVAL_MS);
        }
      } catch {
        // Swallow: a missing summary just leaves the cell blank rather than erroring the row.
      }
    };

    void load();
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [projectId, documentId]);

  if (!summary) return null;

  const { ingested, draftCount, publishedCount, discardedCount } = summary;
  const total = draftCount + publishedCount;

  // Nothing left to publish or discard (all drafts resolved) and at least one published → done.
  if (publishedCount > 0 && draftCount === 0) {
    return (
      <StatusPill backgroundColor={GREEN} fillWidth>
        {`${publishedCount} Task${publishedCount === 1 ? '' : 's'} Published`}
      </StatusPill>
    );
  }

  // At least one task published → show how many are still unpublished out of the total extracted.
  if (publishedCount > 0) {
    return (
      <StatusPill backgroundColor={AMBER} fillWidth>
        {`${draftCount}/${total} Unpublished Tasks`}
      </StatusPill>
    );
  }

  // Ingested → extraction has started (or can be started); nothing published yet.
  if (ingested) {
    return (
      <StatusPill backgroundColor={BLUE} fillWidth>
        Ready for Extraction
      </StatusPill>
    );
  }

  // Still being ingested.
  return (
    <StatusPill backgroundColor={GRAY} fillWidth>
      Pending
    </StatusPill>
  );
}
